<template>
  <div class="pr-1 sm:overflow-auto md:overflow-hidden lg:overflow-hidden">
    <TableWithCondensedContent
      class="mt-4"
      has-cell-link
      cell-link-text="Go to project"
      :table-headers="['Client', 'Project / Budget', 'Budget', 'Updated / Created', 'Status']"
      sort-key="Updated / Created"
      sort-order="desc"
      :table-rows="userProjectsLatest.filter((project) => (project.status !== 'archived')).map((project) => {
        return [
          project.client.name,
          [
            project.name
          ],
          (project.total_travelers ?? '0') + ' pax' + ' &times; ' + (formatCurrency(project.budget_per_traveller) ?? '-'),
          [
            (project.updated_at ? getDateTimeInCurrentTimezone(project.updated_at, 'DD.MM.YYYY') : '-'),
            '&nbsp;&nbsp;&nbsp;' + (project.created_at ? getDateTimeInCurrentTimezone(project.created_at, 'DD.MM.YYYY') : '-')
          ],
          {
            type: 'status-badge',
            value: project.status
          },
          route('projects.show', {id: project.id})
        ];
      })">
      <template #header>
        <Tooltip
          align="left"
          content-class="text-xs italic text-gray-400 hover:text-gray-400 -mb-4 w-max">
          <template #trigger>
            <h2 class="relative w-fit cursor-help text-lg font-medium text-gray-800">
              Your active projects
            </h2>
          </template>
          <template #content>
            Up to 10 of your projects that have been updated within the last 60 days, excluding those from archived projects.
          </template>
        </Tooltip>
      </template>
    </TableWithCondensedContent>

    <TableWithCondensedContent
      class="my-8"
      has-cell-link
      has-search
      has-paginator
      :items-per-page="10"
      search-placeholder="Search projects"
      cell-link-text="Go to project"
      :table-headers="['Project / Client', 'Project / Manager', 'Budget', 'Created', 'Status']"
      sort-key="Created"
      sort-order="desc"
      :table-rows="teamProjects.map((project) => {
        return [
          project.client.name,
          [
            project.name,
            project.manager.name
          ],
          (project.total_travelers ?? '0') + ' pax' + ' &times; ' + (formatCurrency(project.budget_per_traveller) ?? '-'),
          [
            (project.created_at ? getDateTimeInCurrentTimezone(project.created_at, 'DD.MM.YYYY') : '-'),
          ],
          {
            type: 'status-badge',
            value: project.status
          },
          route('projects.show', {id: project.id})
        ];
      })">
      <template #header>
        <Tooltip
          class="mb-2 w-fit"
          align="left"
          content-class="text-xs italic text-gray-400 hover:text-gray-400 -mb-4 w-max">
          <template #trigger>
            <h2 class="relative w-fit cursor-help text-lg font-medium text-gray-800">
              Your team's projects
            </h2>
          </template>
          <template #content>
            Every project (including yours) found in your team, <strong>{{ team.name }}</strong>.
          </template>
        </Tooltip>
      </template>
      <template #button>
        <div class="self-end sm:ml-16 sm:flex-none">
          <button
            type="button"
            class="inline-flex items-center justify-center gap-1 rounded-md border
              border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm
              hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
            @click="showCreateForm = !showCreateForm">
            Add new project
          </button>
        </div>
      </template>
    </TableWithCondensedContent>
  </div>
  <div class="relative mt-5 overflow-hidden rounded-lg bg-white shadow" />
  <Modal
    :show="showCreateForm"
    max-width="md"
    :closeable="true"
    @close="showCreateForm = false">
    <CreateProjectForm @close="showCreateForm = false" />
  </Modal>
  <div class="relative mt-5 overflow-hidden rounded-lg bg-white shadow" />
</template>

<script setup>
/* external */
import {
  ref, useAttrs,
} from 'vue';
import Modal from '@/Jetstream/Modal.vue';

/* local */
import TableWithCondensedContent from '@/Components/Lists/TableWithCondensedContent.vue';
import {
  formatCurrency, formatDateTimeToLocaleDateString, getDateTimeInCurrentTimezone,
} from '@/utils';
import Tooltip from '@/Components/Tooltip.vue';
import CreateProjectForm from '@/Components/CreateProjectForm.vue';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */
defineProps({
  userProjectsLatest: {
    type: Array,
    required: true,
    default: () => [],
  },
  teamProjects: {
    type: Array,
    required: true,
    default: () => [],
  },
});

const attrs = useAttrs();
const { team } = attrs;

const showCreateForm = ref(false);
</script>
